import React, { useCallback, useEffect, useState } from "react";
import Masonry from "react-masonry-component";
import { GalleryElement } from "../../store/Gallery";
import GalleryItem from "./GalleryItem";

const masonryOptions: Masonry.MasonryOptions = {
  transitionDuration: 500,
  resize: true,
};

interface Props {
  elements: GalleryElement[];
  showOverlayOnGalleryElement?: boolean;
  containerClassName: string;
  scrollableElementClassName?: string;
  pageSize?: number;
  onGalleryItemClick?: (element: GalleryElement) => void;
}

const GalleryGrid = ({
  elements,
  containerClassName,
  showOverlayOnGalleryElement,
  scrollableElementClassName,
  pageSize = 6,
  onGalleryItemClick,
}: Props) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentItems, setCurrentItems] = useState<GalleryElement[]>([]);

  const onScroll = useCallback(
    (scrollableElement: Element) => {
      const scrollHeight = scrollableElement.scrollHeight;
      const scrollTop = scrollableElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      const scrollThreshold = 200; // Adjust the threshold as needed

      if (elements.length / pageSize <= currentPage) {
        return;
      }

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        // Load more items when scrolling near the bottom
        setCurrentPage((previousPage) => previousPage + 1);
      }
    },
    [elements, currentPage]
  );

  const onScrollWindow = useCallback(() => {
    const scrollHeight = window.scrollY;
    const scrollTop = document.body.scrollTop;
    const clientHeight = document.documentElement.clientHeight;
    const scrollThreshold = 200; // Adjust the threshold as needed

    if (elements.length / pageSize <= currentPage) {
      return;
    }

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      // Load more items when scrolling near the bottom
      setCurrentPage((previousPage) => previousPage + 1);
    }
  }, [elements, currentPage]);

  useEffect(() => {
    if (currentPage === 1) return;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentItems = elements.slice(startIndex, endIndex);
    setCurrentItems((prevItems) => [...prevItems, ...currentItems]);
  }, [currentPage, elements]);

  useEffect(() => {
    setCurrentPage(1);
    setCurrentItems(elements.slice(0, pageSize));
  }, [elements, pageSize]);

  useEffect(() => {
    window.addEventListener("scroll", onScrollWindow);

    return () => {
      window.removeEventListener("scroll", onScrollWindow);
    };
  }, [onScrollWindow]);

  useEffect(() => {
    const scrollableElement = scrollableElementClassName
      ? document.getElementsByClassName(scrollableElementClassName)?.[0]
      : null;

    if (!scrollableElement) return;

    const scroll = () => onScroll(scrollableElement);

    scrollableElement.addEventListener("scroll", scroll);

    return () => {
      scrollableElement.removeEventListener("scroll", scroll);
    };
  }, [onScroll, scrollableElementClassName]);

  return (
    <div className={containerClassName}>
      <Masonry
        className="gallery-masonry"
        elementType="div"
        enableResizableChildren
        options={masonryOptions}
        disableImagesLoaded={false}
        updateOnEachImageLoad={false}
      >
        {currentItems
          .sort((a, b) => a.number - b.number)
          .map((element: GalleryElement) => (
            <GalleryItem
              key={element.id}
              element={element}
              showOverlay={showOverlayOnGalleryElement}
              onClick={() => {
                onGalleryItemClick?.(element);
              }}
            />
          ))}
      </Masonry>
    </div>
  );
};

export default GalleryGrid;
